/**
 * 23 - Photograph the Convoy (10 §2): no compound - a stretch of road north of the belt, a checkpoint on
 * it, and an overlook among the rocks a dozen metres off the verge. At a stated time a column comes down
 * the road from the north edge; the forty seconds it takes to pass are the window, and twenty of them
 * spent in the overlook, unseen, are the photographs. Arrive late, or be seen at it late, and it is lost.
 *
 * The time is set by `finish` from the walk the finished map offers (helpers3bTime.ts). ROUND4 §1: the column
 * is a real one - lorries on the road with their escort walking beside them, and at the end of the road they
 * are GONE, not turned round. Photographing it is a window that opens once.
 */

import type { EnemyKind } from '../../sim/types';
import { checker, guard, insideRect, structure } from './helpers';
import { flagTargets, RECALL, wokenBy } from './helpers3b';
import { alongRoad, column, type ColumnMan, mainRoad, roadsideHide } from './helpers3bRoad';
import { carefulSeconds, fairTimer, timedRule } from './helpers3bTime';
import { roadRun, vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

const WINDOW_SECONDS = 40;
const DWELL_SECONDS = 20;
const TAG = 'convoyCrosses';
/** Free cells wanted in the overlook: room to lie down in, not a single square. */
const LYING_ROOM = 12;

/** Four men at threat 1, eight at threat 9: an officer leading from threat 4. */
export const convoyMen = (threat: number): number => 4 + Math.floor(threat / 2);
/** Lorries in the column beside them (ROUND4 §1): two, three from threat 5. */
export const convoyLorries = (threat: number): number => (threat >= 5 ? 3 : 2);
/** The column rolls at this, in metres per second: a walking escort keeps up with it. */
const CONVOY_SPEED = 2;

export const photographConvoy: ArchetypeSpec = {
  id: 'photograph-convoy',
  name: 'PHOTOGRAPH THE CONVOY',
  nativeBiome: 'desert',
  baseThreat: 3,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [50, 58], d: [34, 38] },
  river: false,
  // Nothing here has an inside: the goal's patrol walks round the ground, and past the overlook.
  patrolLoop: 'outside',
  breach: { min: 0, max: 0 },

  build(g) {
    const road = mainRoad(g, true);
    const hide = roadsideHide(g);
    // The checkpoint the column is making for: bags across the verge, a shelter, two men looking down the road.
    const roadX = g.ctx.plan.roadEnd.x;
    const far = hide && hide.marker.x < roadX ? 1 : -1;
    const post = { x: roadX + far * 5.5, z: g.rect.z0 + 5 };
    g.map.place('sandbags', post.x, post.z + 2, { length: 4.4, margin: 0.6 });
    structure(g, g.kit.shelter, { within: { x0: post.x - 8, z0: g.rect.z0 + 1, x1: post.x + 8, z1: post.z + 6 }, margin: 1 });
    for (const dx of [-1.2, 1.4]) guard(g, post.x + dx, post.z, g.rng.range(-0.3, 0.3));

    const count = convoyMen(g.threat);
    const soldier: EnemyKind = g.elite ? 'elite' : 'rifleman';
    const men: ColumnMan[] = Array.from({ length: count }, (_, i) => ({ kind: i === 0 && g.threat >= 4 ? 'officer' : soldier }));
    const south = (g.ctx.plan.lines[0]?.z ?? g.ctx.sizeZ - 80) - 8;
    column(g, alongRoad(road, 4 + count, south), men, TAG);
    // R4: the lorries the escort is walking with. They drive the same road and are gone at the end of it.
    const run = roadRun(road, 3, south);
    for (let i = 0; i < convoyLorries(g.threat); i++) {
      vehicle(g, { kind: 'truck', route: run, wakeTag: TAG, speed: CONVOY_SPEED, back: 9 * i });
    }

    const zone = hide?.zone ?? { ...g.inner };
    const markerId = hide?.marker.id ?? -1;
    return {
      objective: {
        kind: 'holdZone', label: 'PHOTOGRAPH THE CONVOY', targetPropIds: [markerId], minFraction: 1,
        zone, dwellSeconds: DWELL_SECONDS, duringWindow: true, dwellUnseen: true,
        rules: [
          {
            type: 'timedEvent', atSeconds: 300, effect: 'convoyCrosses', wakes: TAG,
            windowSeconds: WINDOW_SECONDS, failIfIncomplete: true,
            orders: 'BE IN THE OVERLOOK WHEN THE CONVOY COMES, AND STAY DOWN: 20 SECONDS UNSEEN WHILE IT PASSES, OR IT IS LOST',
            warning: 'THE CONVOY IS DUE IN',
            reason: 'THE CONVOY PASSED UNPHOTOGRAPHED',
          },
          RECALL,
        ],
      },
      finish(def) {
        flagTargets(def, [markerId]);
        const rule = timedRule(def, 'convoyCrosses');
        const at = { x: (zone.x0 + zone.x1) / 2, z: (zone.z0 + zone.z1) / 2 };
        if (rule) rule.atSeconds = carefulSeconds(def, def.player, at);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('photograph-convoy');
    const o = def.objective;
    const zone = o.zone;
    const marker = def.props.find((p) => p.id === o.targetPropIds[0]);
    check(o.kind === 'holdZone' && o.dwellSeconds === DWELL_SECONDS, `objective must be ${DWELL_SECONDS} s held in the overlook`);
    check(o.duringWindow === true && o.dwellUnseen === true, 'time must count only while the convoy passes, and only unseen');
    check(marker?.kind === 'overlookMarker' && marker.target === true, 'the overlook must be marked on the strip map');
    check(zone !== undefined && marker !== undefined && insideRect(zone, marker.x, marker.z), 'the marker must stand in the overlook');
    if (zone && marker) {
      check(probe.standable(zone) >= LYING_ROOM, 'the overlook must have room to lie in');
      check(probe.breach(marker.id) === 0, 'the overlook must be reachable without crossing mines or wire');
      const rocks = def.props.filter((p) => p.kind === 'rock' && Math.abs(p.x - marker.x) < 7 && Math.abs(p.z - marker.z) < 7);
      check(rocks.length >= 6, 'the overlook needs its screen of rocks');
    }
    const rule = timedRule(def, 'convoyCrosses');
    check(rule?.windowSeconds === WINDOW_SECONDS && rule.failIfIncomplete === true, 'the convoy must pass in a 40 s window, and missing it must lose');
    check(DWELL_SECONDS * 2 <= WINDOW_SECONDS, 'the window must leave room for a second try');
    check(
      rule?.orders !== undefined && rule.warning !== undefined && rule.reason !== undefined,
      'the convoy rule must carry its orders, its countdown and its reason',
    );
    const party = wokenBy(def, rule?.wakes ?? TAG);
    const convoy = party.filter((s) => s.route === undefined);
    check(convoy.length === convoyMen(def.threat), `the convoy must number ${convoyMen(def.threat)} at threat ${def.threat}`);
    check(convoy.every((s) => (s.patrol?.length ?? 0) >= 4), 'every man of the convoy must have a road to walk');
    // R4: the lorries they are escorting, which come down the road once and are gone at the end of it.
    const lorries = vehiclesOf(party);
    check(lorries.length === convoyLorries(def.threat), `the column needs ${convoyLorries(def.threat)} lorries`);
    check(vehiclesOf(def.spawns).length === lorries.length, 'every vehicle of this mission belongs to the column');
    for (const lorry of lorries) {
      check(lorry.kind === 'truck' && lorry.route?.loop === false, 'a lorry of the column must drive off the map');
      check((lorry.route?.points.points.length ?? 0) >= 3, 'a lorry needs a road to drive');
    }
    if (zone) {
      const mid = { x: (zone.x0 + zone.x1) / 2, z: (zone.z0 + zone.z1) / 2 };
      const away = ([x, z]: [number, number]): number => Math.sqrt((x - mid.x) * (x - mid.x) + (z - mid.z) * (z - mid.z));
      const nodes = [...convoy.flatMap((s) => s.patrol ?? []), ...lorries.flatMap((s) => s.route?.points.points ?? [])];
      const nearest = Math.min(...nodes.map(away));
      check(nearest <= 24, 'the convoy must pass within sight of the overlook');
      check(fairTimer(def, rule, def.player, mid), 'the convoy must not come before a careful man can be in the overlook');
    }
    return out;
  },
};
